import { ChangeDetectionStrategy, Component, type OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { BreadcrumbsComponent, type BreadcrumbItem } from '@/shared/breadcrumbs/breadcrumbs.component';
import { TenantLogoComponent } from '@/cloud/components/tenant-logo/tenant-logo.component';
import { CloudApiService } from '@/cloud/services/cloud-api.service';

interface NamespaceTab {
    path: string;
    label: string;
    icon: string;
}

/**
 * Tenant administration shell on the management layout: breadcrumbs, a header with the tenant's
 * logo, name and key, and a tab strip over a router outlet. Children: Members (default), API Keys,
 * Domains, AI Agents, Integrations, Settings.
 */
@Component({
    selector: 'app-namespace-detail',
    standalone: true,
    imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, BreadcrumbsComponent, TenantLogoComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './namespace-detail.scss',
    template: `
        <div class="management-page">
            <app-breadcrumbs [home]="breadcrumbHome" [model]="breadcrumbItems()" />
            <header class="overview-header namespace-header">
                <div class="namespace-identity">
                    <app-tenant-logo [tenantId]="namespaceId()" [blobId]="namespace()?.logoBlobId ?? null" [logoUrl]="namespace()?.logoUrl ?? null" style="--tenant-logo-size: 3.5rem" />
                    <div>
                        <p class="overview-eyebrow">Tenant</p>
                        <h1>{{ namespaceName() }}</h1>
                        <p class="overview-description">
                            @if (namespaceKey()) {
                                Key <code>{{ namespaceKey() }}</code> ·
                            }
                            Members, API keys, sign-in domains, AI agent access, integrations and settings for this tenant.
                        </p>
                    </div>
                </div>
            </header>

            <nav class="namespace-tabs" aria-label="Tenant sections">
                @for (tab of tabs(); track tab.path) {
                    <a class="namespace-tab" [routerLink]="tabLink(tab)" routerLinkActive="namespace-tab--active" [routerLinkActiveOptions]="{ exact: true }" [attr.aria-current]="currentChildPath() === tab.path ? 'page' : null">
                        <i [class]="tab.icon" aria-hidden="true"></i>
                        <span>{{ tab.label }}</span>
                    </a>
                }
            </nav>

            <router-outlet />
        </div>
    `
})
export class NamespaceDetail implements OnInit {
    private static readonly CHILD_LABELS: Record<string, string> = {
        '': 'Members',
        'api-keys': 'API Keys',
        domains: 'Domains',
        'ai-agents': 'AI Agents',
        integrations: 'Integrations',
        settings: 'Settings'
    };

    private api = inject(CloudApiService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);

    namespaceId = signal<string>('');
    readonly currentChildPath = signal<string>('');

    namespace = computed(() => {
        const id = this.namespaceId();
        return (this.api.namespaces() ?? []).find((n) => (n.id ?? n.Id) === id) ?? null;
    });
    namespaceName = computed(() => this.namespace()?.name ?? this.namespace()?.Name ?? 'Tenant');
    namespaceKey = computed(() => this.namespace()?.key ?? this.namespace()?.Key ?? '');

    breadcrumbHome = { icon: 'pi pi-home', routerLink: ['/dashboard'] };

    breadcrumbItems = computed<BreadcrumbItem[]>(() => {
        const items: BreadcrumbItem[] = [{ label: 'Tenants', routerLink: ['/admin/tenants'] }];
        items.push({ label: this.namespaceName(), routerLink: ['/admin/tenants', this.namespaceId()] });
        const child = this.currentChildPath();
        if (child) {
            items.push({ label: NamespaceDetail.CHILD_LABELS[child] ?? child, routerLink: ['/admin/tenants', this.namespaceId(), child] });
        }
        return items;
    });

    readonly tabs = computed<NamespaceTab[]>(() => {
        const items: NamespaceTab[] = [
            { path: '', label: 'Members', icon: 'pi pi-users' },
            { path: 'api-keys', label: 'API Keys', icon: 'pi pi-key' },
            { path: 'domains', label: 'Domains', icon: 'pi pi-globe' },
            { path: 'ai-agents', label: 'AI Agents', icon: 'pi pi-sparkles' }
        ];
        if (this.api.hasCapability(this.namespaceId(), 'namespace.manage')) {
            items.push({ path: 'integrations', label: 'Integrations', icon: 'pi pi-github' });
            items.push({ path: 'settings', label: 'Settings', icon: 'pi pi-cog' });
        }
        return items;
    });

    constructor() {
        this.route.paramMap.pipe(takeUntilDestroyed()).subscribe((pm) => {
            this.namespaceId.set(pm.get('namespaceId') ?? '');
        });
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntilDestroyed()
            )
            .subscribe(() => {
                this.currentChildPath.set(this.route.firstChild?.routeConfig?.path ?? '');
            });
    }

    ngOnInit(): void {
        // Namespace names come from the shared cache; roles feed hasCapability.
        this.api.getNamespaces().catch(() => undefined);
        this.api.getRoles().catch(() => undefined);
        this.currentChildPath.set(this.route.firstChild?.routeConfig?.path ?? '');
    }

    tabLink(tab: NamespaceTab): string[] {
        const base = ['/admin/tenants', this.namespaceId()];
        return tab.path ? [...base, tab.path] : base;
    }

    goTo(child: string) {
        const base = ['/admin/tenants', this.namespaceId()];
        this.router.navigate(child ? [...base, child] : base);
    }
}
